import { Box, Button, Dialog, Drawer, Heading, Portal, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '~/api/client'
import { useT } from '~/i18n/use-t'
import { useCompact } from '~/theme/media'
import { Faq, useCanEmailRequest } from './Faq'
import type { FaqId } from './faq-ids'
import { ErrorAlert, useFriendlyError } from './feedback'

/**
 * What the Help sheet shows: `null` is closed, `{}` is open with nothing expanded, `{ faq }` is open on that entry.
 */
export type HelpView = { faq?: FaqId }

/**
 * A help-sheet request for one email: the delete link, or (R110) the continue link, which is no longer sent by itself.
 */
function EmailRequest({
  route,
  copy,
}: {
  route: 'registrationErasureLink' | 'registrationContinueLink'
  copy: 'delete' | 'continue'
}) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const send = useMutation({ mutationFn: () => api(route) })
  return (
    <Stack gap={3}>
      <Text>{t(`help.${copy}Intro`)}</Text>
      {send.isSuccess ? (
        <Text role='status'>{t(`help.${copy}Done`)}</Text>
      ) : (
        <Button
          variant='outline'
          w='full'
          h='auto'
          minH='12'
          py={2}
          whiteSpace='normal'
          loading={send.isPending}
          onClick={() => send.mutate()}
        >
          {t(`help.${copy}Button`)}
        </Button>
      )}
      {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
    </Stack>
  )
}

/** R110: "Email me a link to continue" (to finish later or on another phone or computer). */
export const ContinueByEmail = () => <EmailRequest route='registrationContinueLink' copy='continue' />

/** "Finish later or stop": the two requests, visible without expanding anything. */
function FinishLater() {
  const { t } = useT()
  const applies = useCanEmailRequest()
  if (!applies) return null
  return (
    <Stack gap={4} data-finish-later>
      <Heading as='h3' size='md'>
        {t('faq.group.finish')}
      </Heading>
      <ContinueByEmail />
      <EmailRequest route='registrationErasureLink' copy='delete' />
    </Stack>
  )
}

/** The element the sheet focuses when it opens: the deep-linked entry's trigger, else the title (spec §8.4). */
const initialFocus = (faq: FaqId | undefined) => () =>
  (faq ? document.querySelector<HTMLElement>(`[data-faq-entry="${faq}"] button`) : null) ??
  document.querySelector<HTMLElement>('[data-help-title]')

/**
 * The Help sheet (spec §4): the FAQ, the contact line and, on the registration steps, the requests to finish later or
 * stop. A bottom drawer on a phone, a centred dialog on a computer. It opens over the page, so the page behind it (and
 * what was typed in it) is never lost.
 */
export function HelpSheet({
  view,
  onViewChange,
  flow,
}: {
  view: HelpView | null
  onViewChange: (v: HelpView | null) => void
  /**
   * Offer "Email me a link to continue" and "Send me a link to delete my registration" (only on the registration
   * steps after the email step).
   */
  flow: boolean
}) {
  const { t, config } = useT()
  // A phone (`COMPACT`: narrower than 768 px, or under 500 px tall) gets a bottom drawer, anything else a dialog.
  const compact = useCompact()
  // Keep the last target while the sheet closes, so the entry does not collapse in the middle of the exit animation.
  const last = useRef<{ faq?: FaqId }>({})
  if (view !== null) last.current = view
  const { faq } = last.current
  // Crossing 768 px while the sheet is open swaps the root component: the sheet unmounts and mounts again as the
  // other kind (expanded entries and scroll are lost). Acceptable for a resize or a rotation mid-read.
  const S = (compact ? Drawer : Dialog) as typeof Dialog

  return (
    <S.Root
      open={view !== null}
      onOpenChange={(e) => !e.open && onViewChange(null)}
      lazyMount
      unmountOnExit
      scrollBehavior='inside'
      placement={compact ? 'bottom' : 'center'}
      initialFocusEl={initialFocus(faq)}
    >
      <Portal>
        <S.Backdrop />
        <S.Positioner>
          <S.Content
            bg='bg.panel'
            maxH={compact ? '88dvh' : '85dvh'}
            maxW={compact ? undefined : '36rem'}
            borderTopRadius={compact ? 'xl' : undefined}
          >
            {compact && <Box aria-hidden mx='auto' mt={2} w={10} h={1} borderRadius='full' bg='border.emphasized' />}
            <S.Header>
              <S.Title fontSize='xl' fontWeight='normal' tabIndex={-1} data-help-title>
                {t('help.title')}
              </S.Title>
            </S.Header>
            <S.Body pb={6}>
              <Stack gap={6}>
                <Text>{t(config.org.phone.trim() ? 'help.contact' : 'help.contactNoPhone')}</Text>
                <Faq openId={faq} flow={flow} />
                {flow && <FinishLater />}
              </Stack>
            </S.Body>
            <S.Footer
              pt={3}
              pb='calc(1rem + env(safe-area-inset-bottom, 0px))'
              borderTopWidth='1px'
              borderColor='border'
            >
              <Button w='full' minH='12' onClick={() => onViewChange(null)}>
                {t('common.close')}
              </Button>
            </S.Footer>
          </S.Content>
        </S.Positioner>
      </Portal>
    </S.Root>
  )
}
